/**
 * useNavigationRegistry — Sprint A2 hook.
 *
 * Bron: backend `/api/admin/navigation` (source of truth).
 * Fallback: statische `nav-config.js` (zodat de sidebar niet crasht als de
 * backend traag / offline is).
 *
 * Waarom: elke wijziging aan de nav-tree moet op één centrale plek gebeuren
 * (Registry Pattern). Nieuwe modules registreren zichzelf server-side en
 * verschijnen automatisch in Sidebar + Command Palette + Breadcrumbs.
 *
 * Retourneert:
 *   {
 *     groups: [{key, label, order, icon?, items: [{key, label, icon, ...}]}],
 *     quick_actions: [{key, label, icon, target_key, hash?, permission?}],
 *     itemByKey: {[key]: {item, group}},  // O(1) lookup
 *     loading: bool,
 *     error: null | string,
 *     schema_version: number,
 *   }
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { adminApi, useAuth } from "../../../context/AuthContext";
import { NAV_GROUPS, NAV_ITEMS_FLAT } from "./nav-config";

// Fallback afgeleid uit static nav-config zodat de sidebar de eerste render
// al iets kan tonen.
function buildFallback() {
  const groups = NAV_GROUPS.map((g) => ({
    key: g.key,
    label: g.label,
    order: g.order || 100,
    icon: g.icon,
    items: (g.items || []).map((it) => ({
      key: it.key,
      label: it.label,
      icon: it.icon,
      group_key: g.key,
      permission: it.perm,
      route: `/admin?section=${it.key}`,
      order: it.order || 100,
      description: it.description,
    })),
  }));
  return { groups, quick_actions: [], schema_version: 0 };
}

export function useNavigationRegistry() {
  const { hasPermission } = useAuth();
  // Sprint 2 (Feb 2026) — Guard tegen onnodige 403's:
  // De endpoint /admin/navigation vereist backend-side `website:view`.
  // Voor rollen zonder deze permissie (bv. medewerker/content_manager) zou
  // een unconditional fetch bij elke component-mount hard 403 opleveren en
  // network-noise veroorzaken. In plaats daarvan tonen we voor die
  // gebruikers direct de statische fallback (die is toch al RBAC-gefilterd
  // door de sidebar-code die per item `hasPermission()` checkt).
  const canFetchNav = hasPermission("website:view");
  const [state, setState] = useState(() => ({
    ...buildFallback(),
    loading: canFetchNav,   // geen loading-state als we niet gaan fetchen
    error: null,
  }));
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!canFetchNav) {
      // Fallback is al gezet in initial state; niets meer te doen.
      return () => { mountedRef.current = false; };
    }
    (async () => {
      try {
        const r = await adminApi.get("/admin/navigation");
        if (!mountedRef.current) return;
        const data = r.data || {};
        setState({
          groups: data.groups || [],
          quick_actions: data.quick_actions || [],
          schema_version: data.schema_version || 1,
          loading: false,
          error: null,
        });
      } catch (e) {
        if (!mountedRef.current) return;
        // Fallback naar statische nav zodat de sidebar bruikbaar blijft
        setState((s) => ({
          ...s,
          loading: false,
          error: e?.message || "Kon navigatie niet laden",
        }));
      }
    })();
    return () => {
      mountedRef.current = false;
    };
  }, [canFetchNav]);

  const itemByKey = useMemo(() => {
    const map = {};
    (state.groups || []).forEach((g) => {
      (g.items || []).forEach((it) => {
        map[it.key] = { item: it, group: g };
      });
    });
    return map;
  }, [state.groups]);

  return { ...state, itemByKey };
}

/**
 * Registry-driven breadcrumb resolver — client-side spiegel van de
 * backend `resolve_breadcrumbs()`. Gebruikt itemByKey zodat er GEEN
 * aparte breadcrumb-config bestaat (Single Source of Truth).
 */
export function resolveBreadcrumbsFromRegistry(itemByKey, navKey, entityLabel = null) {
  const crumbs = [{ label: "Backoffice", key: null, active: false }];
  const entry = itemByKey[navKey];
  if (!entry) {
    crumbs.push({ label: navKey || "Onbekend", key: navKey, active: true });
    return crumbs;
  }
  const { item, group } = entry;
  if (group && group.key !== "overzicht") {
    crumbs.push({ label: group.label, key: null, active: false });
  }
  crumbs.push({ label: item.label, key: item.key, active: !entityLabel });
  if (entityLabel) {
    crumbs.push({ label: entityLabel, key: null, active: true });
  }
  return crumbs;
}
